import React, { useState, useCallback, useEffect } from 'react';
import { Chart, Series, Legend, ValueAxis, Tooltip,
  AggregationInterval, ArgumentAxis, Label, ZoomAndPan,
  TickInterval, CommonSeriesSettings, } from 'devextreme-react/chart';
import { SelectBox } from 'devextreme-react/select-box';
import { TagBox } from 'devextreme-react/tag-box';
import { Button } from 'devextreme-react/button';
import { Tooltip as TTooltip } from 'devextreme-react/tooltip';
import { groupBy, calcWeightDataESG, Array_Average, Quartile } from '../../utils/miscUtil';
import { AvgTable } from '../'

const filter_fields = [
  "13", "14", "24", "25",
  "16", "17", "18",
  "19", "20", "21",
  "9",
  "3", "8",
  "10", "23",
  "1", "2", "6", "7", "5",
  "12",
  "22",
  "11",
  "27", "28", "29",
  "30", "31", "33",
];

export default function ESGQuantitativeMetrics(props) {
  const [originalData, setOriginalData] = useState();
  const [data, setData] = useState();
  const [companiesList, setCompaniesList] = useState([]);
  const [allCompaniesList, setAllCompaniesList] = useState([]);
  const [selectedCompaniesList, setSelectedCompaniesList] = useState([]);
  const [reportingCompanies, setReportingCompanies] = useState([]);
  const [metrics, setMetrics] = useState();
  const [metric, setMetric] = useState(null);
  const [unit, setUnit] = useState(null);
  const [boundaries, setBoundaries] = useState([]);
  const [boundary, setBoundary] = useState(null);
  const [weightData, setWeightData] = useState({});
  const [weight, setWeight] = useState("Oil & Gas Weighted");
  const [avgData, setAvgData] = useState(null);

  useEffect(() => {
    let wd = calcWeightDataESG(props.data, (parseInt(props.year)-1).toString());
    setWeightData(wd);
    var fd = props.data.filter((e) => {return filter_fields.includes(e["Identifier"])});
    var fd1 = groupBy(fd, 'company_name')
    setAllCompaniesList(Object.keys(fd1).sort())
    for (var i=0; i<fd.length; i++) {
      fd[i]['company_'+fd[i].company_name.toLowerCase().replaceAll(" ","_")] = parseFloat(fd[i].value?.replaceAll(",","")) || null;
      fd[i]['date'] = new Date(fd[i]['year'], 0)
    }
    setOriginalData(fd);
    fd = groupBy(fd, 'Metrics')
    setMetrics(Object.keys(fd).sort());
    fd = props.data.filter((e) => {return filter_fields.includes(e["Identifier"])});
    fd = groupBy(fd, 'Category')
    setBoundaries(Object.keys(fd).sort())
  }, [props.data, props.year]);



  const customizeTooltip = useCallback((pointInfo) => {
    var cn = ""
    if (pointInfo.seriesName.includes("Series")) {
      cn = "Company "+pointInfo.seriesName.split(" ")[1]
    } else {
      cn = pointInfo.seriesName
    }
    const y = pointInfo.argument.toLocaleString('default', {year: 'numeric'});
    var txt = ''
    txt = `<div><div class="tooltip-header">${y}</div><div class="tooltip-body">`
    txt = txt+`<div>${cn}: ${pointInfo.value}</div>`
    txt=txt+`</div></div>`
    return {
      html: txt,
    }
  }, []);

  const customizeTooltip2 = useCallback((pointInfo) => {
    const y = pointInfo.argument.toLocaleString('default', { year: 'numeric'});
    var txt = ''
    txt = `<div><div class="tooltip-header">${y} - ${pointInfo.value} Companies</div></div>`
    return {
      html: txt,
    }
  }, []);

  const onMetricChanged = useCallback((e) => {
    setMetric(e.value);
    let u = originalData.filter((it) => it["Metrics"].includes(e.value))
    if (u.length) {
      u = Object.keys(groupBy(u,"Units"))
      setUnit(u[0]);
    } else {
      setUnit(null);
    }

  }, [setMetric, setUnit, originalData])

  const onBoundaryChanged = useCallback((e) => {
    setBoundary(e.value);
    if (e.value) {
      if (e.value.length) {
        let ml = Object.keys(groupBy(originalData.filter((it) => e.value.includes(it["Category"])),"Metrics"))
        if (!ml.includes(metric)) {
          setMetric(null)
        }
        setMetrics(ml.sort());
      } else {
        setMetrics(Object.keys(groupBy(originalData,"Metrics")).sort());
      }
    }

  }, [setBoundary, setMetrics, originalData, metric, setMetric])

  const onCompaniesChanged = useCallback((e) => {
    setSelectedCompaniesList(e.value);
  }, [setSelectedCompaniesList])

  const onWeightChanged = useCallback((e) => {
    setWeight(e.value);
  }, [setWeight])

  useEffect(() => {
    if (!originalData) {
      return;
    }
    var fd = originalData
    if (selectedCompaniesList.length) {
      fd = fd.filter((it) => selectedCompaniesList.includes(it['company_name']));
    }
    fd = fd.filter((it) => it['Metrics']===metric);

    var fd1 = groupBy(fd,"company_name");
    fd1 = Object.keys(fd1)
    var i;

    fd1 = fd1.map((key) => ({company_name:key}));
    fd1 = fd1.sort((a,b) => (a.company_name > b.company_name) ? 1 : ((b.company_name > a.company_name) ? -1 : 0));
    if (weight !== "Oil & Gas Weighted") {
      fd1 = fd1.filter((it) => weight.includes(weightData[it["company_name"]]))
    }
    setCompaniesList(fd1);

    fd1 = groupBy(fd,"year");
    for (const [key] of Object.entries(fd1)) {
      var vals = [];
      var v = 0;
      for (i=0; i<fd1[key].length; i++){
        v = parseFloat(fd1[key][i]["value"]);
        if (!isNaN(v)) {
          vals.push(v)
        }
      }

      fd1[key] = {avg: Array_Average(vals), q25: Quartile(vals, 0.25), q50: Quartile(vals, 0.50), q75: Quartile(vals, 0.75)};
    }
    fd1 = Object.keys(fd1).map((key) => ({date: new Date(key, 0), ...fd1[key]}));
    setAvgData(fd1);
    fd.push(...fd1)
    setData(fd);

    fd = groupBy(fd.filter((it) => !isNaN(parseFloat(it['value']?.replaceAll(",","")))),"year");
    if (weight !== "Oil & Gas Weighted") {
    }
    fd = Object.keys(fd).map((key) => {
      let fdd = fd[key]
      if (weight !== "Oil & Gas Weighted") {
        fdd = fdd.filter((it) => weight.includes(weightData[it["company_name"]]))
      }
      return {date: new Date(key, 0) , count: fdd.length}
    });
    setReportingCompanies(fd);

  }, [props.data, props.year, metric, boundary, selectedCompaniesList, weight, weightData, originalData]);

  const renderContent = () => {
      return (
          <div>Gas/Oil Weighting is calculated from Gross Annual Oil Production and<br/>
          Gross Annual Production - As Reported Under Subpart W. Companies that <br/>
          did not report both of these metrics were not assigned a weighting.</div>
      );
  }

  return (
    <div className="d-flex flex-column">
      <div className="d-flex flex-row">
        <TagBox
            dataSource={allCompaniesList}
            value={selectedCompaniesList}
            showDropDownButton={true}
            width="50%"
            activeStateEnabled={true}
            multiline={false}
            maxDisplayedTags={1}
            showSelectionControls={true}
            onValueChanged={onCompaniesChanged}
            label={"Company"}
            labelMode="floating"
            placeholder={"Select Companies"}
        />
        <div className="mx-1"/>
        <TagBox
            dataSource={boundaries}
            value={boundary}
            width="50%"
            showDropDownButton={true}
            activeStateEnabled={true}
            onValueChanged={onBoundaryChanged}
            multiline={false}
            maxDisplayedTags={1}
            showSelectionControls={true}
            label={"Category"}
            labelMode="floating"
            placeholder={"Select Category"}
        />
      </div>
      <div className="d-flex flex-row align-items-end">
        <SelectBox
            dataSource={metrics}
            showDropDownButton={true}
            width="100%"
            activeStateEnabled={true}
            placeholder={"Select Metric"}
            label={"Metric"}
            labelMode="floating"
            onValueChanged={onMetricChanged}
        />
        <div className="mx-1"/>
        <SelectBox
            dataSource={["Gas Weighted", "Oil Weighted", "Oil & Gas Weighted"]}
            value={weight}
            width="250px"
            showDropDownButton={true}
            activeStateEnabled={true}
            onValueChanged={onWeightChanged}
            label={"Gas/Oil Weighted"}
            labelMode="floating"
            placeholder={"Gas/Oil Weighted"}
            noDataText={"Select a metric first"}
        />
        <div>
          <Button
            id="moreweight"
            icon='fas fa-info-circle'
            stylingMode="text"
            type="normal"
          />
          <TTooltip
              target="#moreweight"
              showEvent="mouseenter"
              hideEvent="mouseleave"
              contentRender={renderContent}
          />
        </div>
      </div>
      <hr className="mt-1 mb-2"></hr>
      <div className="d-flex flex-row">
        <div className="d-flex flex-column w-100">
          {metric ? <h5>{metric}</h5>:<h5>Select a metric...</h5>}
          {unit && <p>({unit})</p>}
          <Chart
            id="chart"
            dataSource={data}
            height="50vh"
          >
            <ArgumentAxis aggregatedPointsPosition="crossTicks">
              <AggregationInterval years={1} />
              <TickInterval years={1} />
            </ArgumentAxis>
            <CommonSeriesSettings
              argumentField="date"
            >
            </CommonSeriesSettings>
            {companiesList.map((e) => (<Series
              type="bar"
              key={e.company_name}
              valueField={"company_"+e.company_name.toLowerCase().replaceAll(" ","_")}
              showInLegend={false}
              name={e.company_name}
              color="rgba(0, 162, 200, 0.8)"
              ignoreEmptyPoints={true}
            />))}
            <Series
              type="line"
              valueField="avg"
              name="Average"
              color="rgba(20, 77, 109, 1)"
              dashStyle="solid"
            />
            <Series
              type="line"
              valueField="q25"
              name="1st Quartile"
              color="rgba(20, 77, 109, 0.25)"
              dashStyle="dot"
            />
            <Series
              type="line"
              valueField="q50"
              name="2nd Quartile"
              color="rgba(20, 77, 109, 0.5)"
              dashStyle="dash"
            />
            <Series
              type="line"
              valueField="q75"
              name="3rd Quartile"
              color="rgba(20, 77, 109, 0.75)"
              dashStyle="longDash"
            />
            <ValueAxis showZero={false} />

            <Legend
              verticalAlignment="bottom"
              horizontalAlignment="center"
              itemTextPosition="bottom"
            />
            <ZoomAndPan
              valueAxis="both"
              argumentAxis="both"
              dragToZoom={true}
              allowMouseWheel={true}
              panKey="shift"
            />
            <Tooltip
              enabled={true}
              customizeTooltip={customizeTooltip}
            />
          </Chart>
        </div>
      </div>
      <div className="d-flex flex-row mt-3">
        <div className="d-flex flex-column me-4" style={{width:"30%"}}>
          <Chart
            id="chart"
            dataSource={reportingCompanies}
            title={"Reporting Companies"}
            rotated={true}
            height="200px"
          >
            <ArgumentAxis aggregatedPointsPosition="crossTicks">
              <AggregationInterval years={1} />
              <TickInterval years={1} />
            </ArgumentAxis>
            <CommonSeriesSettings
              argumentField="date"
            >
            </CommonSeriesSettings>
            <Series
              type="bar"
              valueField={"count"}
              color="rgba(0, 162, 200, 0.8)"
            >
              <Label visible={true}/>

            </Series>

            <ValueAxis showZero={true} allowDecimal={false} tickInterval={5}>
            </ValueAxis>
            <Legend visible={false} />
            <Tooltip
              enabled={true}
              customizeTooltip={customizeTooltip2}
            />
          </Chart>
        </div>

        <div className="d-flex flex-column" style={{width:"70%"}}>
          <AvgTable data={avgData}/>
        </div>
      </div>
    </div>
  );
}
